import { Card } from "components/ui/card";
import { Pause, Play, Trash } from "lucide-react";
import { logoFlipFlop } from "assets";
import { useRef, useState } from "react";
import { Service } from "utils/services";

import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import useDialogUploadEditPostStore from "store/dialog/useDialogUploadEditPostContentStore";

interface UploadEditSettingMusicPlayerProps {
  music: any;
  onAddMusic: () => void;
}

export function UploadEditSettingMusicPlayer({ music, onAddMusic }: UploadEditSettingMusicPlayerProps) {
  const [currentSongUrl, setCurrentSongUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { setMusic } = useDialogUploadEditPostStore();

  const onClickedRemovieMusic = () => {
    setMusic(null);
  };

  return (
    <>
      <Card key={music.id} className="p-2 rounded-md w-full cursor-pointer me-2 relative">
        <button
          onClick={onClickedRemovieMusic}
          className="absolute -top-3 -left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
        >
          <Trash size={16} />
        </button>

        <div className="flex gap-3 items-center w-full">
          {/* Image Thumbnail */}
          <div className="w-14 h-14">
            <img src={music.thumbnail ?? logoFlipFlop} alt={music.name} className="w-14 h-14 rounded-md object-cover" />
          </div>

          {/* Sound Details (clickable for play/pause too) */}
          <div
            className="flex-1 space-y-1"
            onClick={(e) => {
              e.stopPropagation();
              if (currentSongUrl === music.sound_url && isPlaying) {
                audioRef.current?.pause();
              }
              onAddMusic();
            }}
          >
            <p className="text-md font-semibold line-clamp-1">{music?.sound_title}</p>
            <p className="text-muted-foreground text-xs font-semibold line-clamp-1">
              {music?.sound_singer || "Unknown"} • {music?.sound_genres || "None"} • {Service.formatSoundDuration(music.sound_duration)} • {music?.usage_count} Used
            </p>
            <p className="text-muted-foreground text-sm font-semibold line-clamp-1">{music?.hashtag}</p>
          </div>

          {/* Play/Pause with Circular Progress on Right */}
          <div className="w-10 h-10 ml-auto">
            <CircularProgressbarWithChildren
              value={currentSongUrl === music.sound_url && duration ? (currentTime / duration) * 100 : 0}
              styles={buildStyles({
                pathColor: "hsl(var(--primary))",
                trailColor: "hsl(var(--muted))",
                strokeLinecap: "round",
              })}
              strokeWidth={6}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentSongUrl === music.sound_url && isPlaying) {
                    audioRef.current?.pause();
                  } else if (currentSongUrl === music.sound_url && !isPlaying) {
                    audioRef.current?.play();
                  } else {
                    setCurrentSongUrl(music.sound_url);
                    setIsPlaying(true);
                  }
                }}
                className="text-white bg-black/60 rounded-full w-7 h-7 flex items-center justify-center"
              >
                {currentSongUrl === music.sound_url && isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
            </CircularProgressbarWithChildren>
          </div>
        </div>
      </Card>

      {currentSongUrl && (
        <audio
          ref={audioRef}
          src={currentSongUrl}
          autoPlay
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={() => {
            setCurrentTime(audioRef.current?.currentTime || 0);
          }}
          onLoadedMetadata={() => {
            setDuration(audioRef.current?.duration || 0);
          }}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />
      )}
    </>
  );
}

